import {
	FaPaperPlane,
	FaStar,
	FaShieldAlt,
	FaSms,
	FaChartLine,
	FaThumbsUp,
	FaRegStar,
} from "react-icons/fa";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../api";
import { baseUrl } from "../../Environments/environment";
import { useProperty } from "../../Contexts/PropertyContext/PropertyContext";

import Milestone from "./Milestone";

import {
	ResponsiveContainer,
	LineChart,
	Line,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	Tooltip,
	Cell,
	LabelList,
	AreaChart,
	Area,
	CartesianGrid,
} from "recharts";

import {
	MessageSquare,
	Frown,
	Smile,
	Calendar,
	Clock,
	Sparkles,
	Info,
	Sliders,
	AlertCircle,
	Star,
	Trophy,
	Users,
	ChevronDown,
} from "lucide-react";

// ─── Custom Peak Callout Label ───────────────────────────────────────────────
const CustomPeakLabel = (props) => {
	const { x, y, value, index, data } = props;
	if (!data || data.length === 0) return null;
	const maxValue = Math.max(...data.map(d => d.count || 0));
	if (value === maxValue && maxValue > 0) {
		return (
			<g key={`peak-${index}`}>
				<rect x={x - 14} y={y - 24} width={28} height={14} rx={3} fill="#F97316" />
				<text x={x} y={y - 14} fill="#FFF" fontSize={8} fontWeight="bold" textAnchor="middle">
					{value}
				</text>
				<path d={`M${x} ${y - 9} L${x - 2.5} ${y - 12} L${x + 2.5} ${y - 12} Z`} fill="#F97316" />
			</g>
		);
	}
	return null;
};

// ─── Custom Bar Callout Label ────────────────────────────────────────────────
const CustomBarLabel = (props) => {
	const { x, y, width, value, index, data } = props;
	if (!data || data.length === 0) return null;
	const maxValue = Math.max(...data.map(d => d.count || 0));
	const isHighest = value === maxValue && maxValue > 0;
	
	if (isHighest) {
		return (
			<g key={`bar-label-${index}`}>
				{/* Floating number above star */}
				<text x={x + width / 2} y={y - 28} fill="#6366F1" fontSize={10} fontWeight="bold" textAnchor="middle">
					{value.toLocaleString()}
				</text>
				{/* Star badge below number, above bar */}
				<g transform={`translate(${x + width / 2 - 7}, ${y - 22})`}>
					<circle cx={7} cy={7} r={7} fill="#FFF" stroke="#6366F1" strokeWidth={1.5} />
					<polygon 
						points="7,2.5 8.4,5.8 11.8,6 9.1,8.3 10,11.6 7,9.8 4,11.6 4.9,8.3 2.2,6 5.6,5.8" 
						fill="#6366F1"
					/>
				</g>
			</g>
		);
	}
	
	return (
		<text x={x + width / 2} y={y - 8} fill="#9CA3AF" fontSize={10} fontWeight="semibold" textAnchor="middle">
			{value.toLocaleString()}
		</text>
	);
};

// ─── Custom Tooltip ──────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload }) => {
	if (active && payload && payload.length) {
		const data = payload[0].payload;
		return (
			<div className="bg-white border border-gray-200 p-2.5 rounded-xl shadow-lg text-xs">
				<p className="font-semibold text-gray-700">{data.weekRange || data.name}</p>
				<p className="text-[#F97316] font-semibold mt-0.5">
					count : <span className="font-bold">{data.count}</span>
				</p>
			</div>
		);
	}
	return null;
};

// ─── Loading Overlay ──────────────────────────────────────────────────────────
const ReviewsLoadingOverlay = () => (
	<div className="absolute inset-0 z-50 flex flex-col items-center justify-start bg-gray-50/65 backdrop-blur-[1px] pt-[20vh] cursor-wait">
		<div className="flex items-center gap-3 bg-white border border-gray-200/80 px-5 py-3 rounded-2xl shadow-xl">
			<div className="w-5 h-5 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
			<span className="text-xs font-bold text-gray-700 tracking-wide">Reviews data loading...</span>
		</div>
	</div>
);

// ─── Skeleton ────────────────────────────────────────────────────────────────
const AnalyticsSkeleton = () => (
	<div className="p-6 bg-gray-50 min-h-screen">
		<div className="flex items-center gap-2 pl-2 mb-6">
			<div className="h-6 w-6 bg-gray-200 rounded animate-pulse" />
			<div className="h-6 w-48 bg-gray-200 rounded animate-pulse" />
		</div>

		<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
			{[1, 2, 3, 4].map((i) => (
				<div
					key={i}
					className="bg-white p-5 rounded-xl shadow flex justify-between items-center animate-pulse"
				>
					<div className="flex-1">
						<div className="h-4 w-32 bg-gray-200 rounded mb-2" />
						<div className="h-8 w-20 bg-gray-300 rounded mb-2" />
						<div className="h-3 w-24 bg-gray-200 rounded" />
					</div>
					<div className="h-12 w-12 bg-gray-200 rounded-lg" />
				</div>
			))}
		</div>

		<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
			{[1, 2, 3].map((i) => (
				<div
					key={i}
					className="bg-white p-5 rounded-xl shadow flex justify-between items-center animate-pulse"
				>
					<div className="flex-1">
						<div className="h-4 w-32 bg-gray-200 rounded mb-2" />
						<div className="h-8 w-20 bg-gray-300 rounded mb-2" />
						<div className="h-3 w-24 bg-gray-200 rounded" />
					</div>
					<div className="h-12 w-12 bg-gray-200 rounded-lg" />
				</div>
			))}
		</div>
	</div>
);

// ─── Reviews Per Team Member ─────────────────────────────────────────────────
function ReviewsPerUserTable({ reviewsPerUser }) {
	if (reviewsPerUser.length === 0) {
		return (
			<div className="bg-white p-5 rounded-xl shadow mt-6">
				<h3 className="font-semibold text-gray-700 text-base mb-4 flex items-center gap-2">
					👤 Reviews Sent by Team
				</h3>
				<p className="text-gray-500 text-sm">No data available</p>
			</div>
		);
	}

	return (
		<div className="bg-white p-5 rounded-xl shadow mt-6">
			<h3 className="font-semibold text-gray-700 text-base mb-4 flex items-center gap-2">
				👤 Reviews Sent by Team
			</h3>
			<div className="divide-y">
				{reviewsPerUser.map((item) => {
					const user = item.user;
					return (
						<div
							key={user._id}
							className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between"
						>
							<div className="flex min-w-0 items-center gap-3">
								<div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold text-sm">
									{user.firstName?.[0]}
								</div>
								<div className="min-w-0">
									<p className="font-medium text-gray-800 break-words text-sm">
										{user.firstName} {user.lastName}
									</p>
									<p className="text-xs text-gray-500 break-words">
										{user.email}
									</p>
								</div>
							</div>

							<div className="flex items-center gap-6 self-start sm:self-auto sm:flex-shrink-0">
								<div className="text-right">
									<p className="text-indigo-600 font-semibold text-sm">
										{item.reviewCount}
									</p>
									<p className="text-xs text-gray-500">Reviews</p>
								</div>
								<div className="text-right">
									<div className="flex items-center gap-1 text-yellow-500 font-semibold text-sm">
										<FaStar className="text-xs" />
										{item.avgRating?.toFixed(1) || "0.0"}
									</div>
									<p className="text-xs text-gray-500 text-right">Avg Rating</p>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ReviewDashboard() {
	const { selectedPropertyId } = useProperty();
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(true);
	const [isFirstLoad, setIsFirstLoad] = useState(true);
	const [isUpdatingPeriod, setIsUpdatingPeriod] = useState(false);

	const [negativeReviews, setNegativeReviews] = useState(0);
	const [positiveReviews, setPositiveReviews] = useState(0);
	const [totalSmsSent, setTotalSmsSent] = useState(0);
	const [totalSmsDelivered, setTotalSmsDelivered] = useState(0);
	const [totalReviewsCount, setTotalReviewsCount] = useState(0);
	const [negativeReviewsAvoided, setNegativeReviewsAvoided] = useState(0);

	// Revamp metrics state
	const [metrics, setMetrics] = useState({
		overall: { total: 0, below3: 0, above4: 0 },
		thisPeriod: { total: 0, below3: 0, above4: 0 },
		today: { total: 0, below3: 0, above4: 0 },
		highlights: { topMonth: "No ratings", topWeek: "No ratings" },
		sparklines: { overall: [0, 0, 0, 0, 0, 0, 0], below3: [0, 0, 0, 0, 0, 0, 0], above4: [0, 0, 0, 0, 0, 0, 0] },
		last3MonthsBars: [],
		last12WeeksLines: []
	});

	const [reviewsPerUser, setReviewsPerUser] = useState([]);
	const [filter, setFilter] = useState("thismonth");
	const [customStart, setCustomStart] = useState("");
	const [customEnd, setCustomEnd] = useState("");

	const getDateRange = (filterOption, start, end) => {
		let startDate = "";
		let endDate = "";
		const now = new Date();

		switch (filterOption) {
			case "today": {
				const d = new Date();
				d.setHours(0, 0, 0, 0);
				startDate = d.toISOString();
				const e = new Date();
				e.setHours(23, 59, 59, 999);
				endDate = e.toISOString();
				break;
			}
			case "yesterday": {
				const d = new Date();
				d.setDate(d.getDate() - 1);
				d.setHours(0, 0, 0, 0);
				startDate = d.toISOString();
				const e = new Date();
				e.setDate(e.getDate() - 1);
				e.setHours(23, 59, 59, 999);
				endDate = e.toISOString();
				break;
			}
			case "last7days": {
				const d = new Date();
				d.setDate(d.getDate() - 6);
				d.setHours(0, 0, 0, 0);
				startDate = d.toISOString();
				endDate = now.toISOString();
				break;
			}
			case "last30days": {
				const d = new Date();
				d.setDate(d.getDate() - 29);
				d.setHours(0, 0, 0, 0);
				startDate = d.toISOString();
				endDate = now.toISOString();
				break;
			}
			case "thismonth": {
				const d = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
				startDate = d.toISOString();
				endDate = now.toISOString();
				break;
			}
			case "custom": {
				if (start) {
					const d = new Date(start);
					d.setHours(0, 0, 0, 0);
					startDate = d.toISOString();
				}
				if (end) {
					const d = new Date(end);
					d.setHours(23, 59, 59, 999);
					endDate = d.toISOString();
				}
				break;
			}
			default:
				break;
		}
		return { startDate, endDate };
	};

	useEffect(() => {
		if (!selectedPropertyId) return;
		fetchAll();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPropertyId]);

	useEffect(() => {
		if (isFirstLoad) {
			setIsFirstLoad(false);
			return;
		}
		if (filter === "custom" && (!customStart || !customEnd)) return;
		fetchPeriodMetrics();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filter, customStart, customEnd]);

	const fetchAll = async () => {
		setIsLoading(true);
		await Promise.allSettled([
			fetchMetrics(),
			fetchReviewsPerUser(),
		]);
		setIsLoading(false);
	};

	const fetchPeriodMetrics = async () => {
		setIsUpdatingPeriod(true);
		try {
			const token = localStorage.getItem("accessToken");
			const { startDate, endDate } = getDateRange(filter, customStart, customEnd);
			const response = await apiClient.get(
				`${baseUrl}/reviews/metrics/${selectedPropertyId}`,
				{
					headers: { Authorization: `Bearer ${token}` },
					params: { startDate, endDate, onlyPeriod: true }
				}
			);
			const d = response?.data?.result;
			if (d) {
				setMetrics(prev => ({
					...prev,
					thisPeriod: d.thisPeriod || prev.thisPeriod,
					prevPeriod: d.prevPeriod || prev.prevPeriod
				}));
			}
		} catch (error) {
			console.error("Error fetching period metrics", error);
		} finally {
			setIsUpdatingPeriod(false);
		}
	};

	const fetchMetrics = async () => {
		try {
			const token = localStorage.getItem("accessToken");
			const { startDate, endDate } = getDateRange(filter, customStart, customEnd);
			const response = await apiClient.get(
				`${baseUrl}/reviews/metrics/${selectedPropertyId}`,
				{
					headers: { Authorization: `Bearer ${token}` },
					params: { startDate, endDate }
				}
			);
			const d = response?.data?.result;
			if (d) {
				setNegativeReviews(d.negativeReviews ?? 0);
				setPositiveReviews(d.positiveReviews ?? 0);
				setTotalSmsSent(d.totalSmsSent ?? 0);
				setTotalSmsDelivered(d.totalSmsDelivered ?? 0);
				setTotalReviewsCount(d.totalReviewsCount ?? 0);
				setNegativeReviewsAvoided(d.negativeReviews ?? 0);

				const getWeekRange = (weeksAgo) => {
					const dateObj = new Date();
					dateObj.setDate(dateObj.getDate() - weeksAgo * 7);

					const simple = new Date(dateObj.getFullYear(), 0, 4);
					const dayOfWeek = simple.getDay() || 7;
					const mondayOfWeek1 = new Date(simple.getTime() - (dayOfWeek - 1) * 24 * 60 * 60 * 1000);
					const msDiff = dateObj.getTime() - mondayOfWeek1.getTime();
					const weekNum = Math.floor(msDiff / (7 * 24 * 60 * 60 * 1000)) + 1;
					const yearNum = dateObj.getFullYear();

					const getDateOfISOWeek = (w, y) => {
						const simp = new Date(y, 0, 4);
						const dow = simp.getDay();
						const dowISO = dow === 0 ? 7 : dow;
						const mon = new Date(simp.getTime() - (dowISO - 1) * 24 * 60 * 60 * 1000);
						return new Date(mon.getTime() + (w - 1) * 7 * 24 * 60 * 60 * 1000);
					};

					const monday = getDateOfISOWeek(weekNum, yearNum);
					const sunday = new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000);

					const shortMonthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
					return `${shortMonthNames[monday.getMonth()]} ${monday.getDate()} - ${shortMonthNames[sunday.getMonth()]} ${sunday.getDate()}, ${monday.getFullYear()}`;
				};

				const rawLines = d.last12WeeksLines || [];
				const enrichedLines = rawLines.map((item, index) => {
					if (item.weekRange) return item;
					const weeksAgo = 11 - index;
					return {
						...item,
						weekRange: getWeekRange(weeksAgo)
					};
				});

				setMetrics({
					overall: d.overall || { total: 0, below3: 0, above4: 0 },
					thisPeriod: d.thisPeriod || { total: 0, below3: 0, above4: 0 },
					prevPeriod: d.prevPeriod || { total: 0, below3: 0, above4: 0 },
					today: d.today || { total: 0, below3: 0, above4: 0 },
					yesterday: d.yesterday || { total: 0, below3: 0, above4: 0 },
					highlights: d.highlights || { topMonth: "No ratings", topWeek: "No ratings" },
					sparklines: d.sparklines || { overall: [0, 0, 0, 0, 0, 0, 0], below3: [0, 0, 0, 0, 0, 0, 0], above4: [0, 0, 0, 0, 0, 0, 0] },
					last3MonthsBars: d.last3MonthsBars || [],
					last12WeeksLines: enrichedLines
				});
			}
		} catch (error) {
			console.error("Error fetching metrics", error);
		}
	};

	const fetchReviewsPerUser = async () => {
		try {
			const token = localStorage.getItem("accessToken");
			const { startDate, endDate } = getDateRange(filter, customStart, customEnd);
			const response = await apiClient.get(
				`${baseUrl}/review/${selectedPropertyId}/reviewsPerUser`,
				{
					headers: { Authorization: `Bearer ${token}` },
					params: { startDate, endDate }
				}
			);
			setReviewsPerUser(response?.data?.result?.reviews || []);
		} catch (error) {
			console.error("Error fetching reviews per user", error);
		}
	};

	// Convert sparkline arrays to Recharts readable data format
	const overallSparklineData = (metrics.sparklines?.overall || [0, 0, 0, 0, 0, 0, 0]).map(val => ({ count: val }));
	const below3SparklineData = (metrics.sparklines?.below3 || [0, 0, 0, 0, 0, 0, 0]).map(val => ({ count: val }));
	const above4SparklineData = (metrics.sparklines?.above4 || [0, 0, 0, 0, 0, 0, 0]).map(val => ({ count: val }));

	// Safe formatting for dates
	const getFormattedDateRangeLabel = () => {
		const { startDate, endDate } = getDateRange(filter, customStart, customEnd);
		if (!startDate) return "All Time";
		const formatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
		const startLabel = new Date(startDate).toLocaleDateString('en-US', formatOptions);
		const endLabel = new Date(endDate || new Date()).toLocaleDateString('en-US', formatOptions);
		return `${startLabel} – ${endLabel}`;
	};

	const maxMonthBar = (metrics.last3MonthsBars || []).reduce((max, b) => b.count > (max?.count || 0) ? b : max, null);
	const topMonthCount = maxMonthBar ? maxMonthBar.count : 0;

	const maxWeekLine = (metrics.last12WeeksLines || []).reduce((max, w) => w.count > (max?.count || 0) ? w : max, null);
	const topWeekCount = maxWeekLine ? maxWeekLine.count : 0;

	return (
		<div className="p-4 sm:p-6 bg-gray-50 min-h-screen relative">
			{isLoading && <ReviewsLoadingOverlay />}
			{/* ── Page Header ── */}
			<div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4 pb-2">
				<div>
					<h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 tracking-tight">Reviews Overview</h1>
					<p className="text-sm text-gray-500 mt-1 font-medium">
						Track your review performance at a glance
					</p>
				</div>
				<div className="flex items-center gap-3">
					<button
						onClick={() => navigate("/dashboard/recommendations")}
						className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white px-4 py-2.5 rounded-xl text-xs font-semibold shadow-md hover:shadow-lg transition-all duration-205"
					>
						<Sparkles className="w-4 h-4" /> Secure Get Recommendations
					</button>
				</div>
			</div>

			{/* ── Row 1: Overall KPI Cards with Sparklines ── */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 animate-slideIn">
				{/* 1. Overall Total */}
				<div className="bg-white border border-gray-200/80 rounded-3xl p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 flex flex-col justify-between relative overflow-hidden group">
					<div className="flex items-start justify-between gap-4">
						<div className="flex items-start gap-4">
							<div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 shadow-[0_4px_12px_rgba(37,99,235,0.2)]">
								<Star className="w-5 h-5 fill-current" />
							</div>
							<div>
								<span className="text-xs font-semibold text-gray-700 block leading-tight">Total Ratings</span>
								<span className="text-[10px] font-medium text-gray-500 block mt-0.5 leading-tight">All-time ratings received</span>
								<span className="text-2xl sm:text-3xl font-semibold text-gray-900 mt-2 block leading-none">{metrics.overall.total.toLocaleString()}</span>
							</div>
						</div>
						<div className="w-24 h-12 flex-shrink-0 self-center">
							<ResponsiveContainer width="100%" height="100%">
								<AreaChart data={overallSparklineData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
									<defs>
										<linearGradient id="colorOverall" x1="0" y1="0" x2="0" y2="1">
											<stop offset="5%" stopColor="#3B82F6" stopOpacity={0.25}/>
											<stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
										</linearGradient>
									</defs>
									<Area type="monotone" dataKey="count" stroke="#3B82F6" strokeWidth={1.8} fillOpacity={1} fill="url(#colorOverall)" dot={false} />
								</AreaChart>
							</ResponsiveContainer>
						</div>
					</div>
				</div>

				{/* 2. Overall Below 3 */}
				<div className="bg-white border border-gray-200/80 rounded-3xl p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 flex flex-col justify-between relative overflow-hidden group">
					<div className="flex items-start justify-between gap-4">
						<div className="flex items-start gap-4">
							<div className="w-12 h-12 rounded-full bg-red-50 border border-red-200 text-red-500 flex items-center justify-center flex-shrink-0">
								<Frown className="w-5 h-5" />
							</div>
							<div>
								<span className="text-xs font-semibold text-gray-700 block leading-tight">Below 3 Star</span>
								<span className="text-[10px] font-medium text-gray-500 block mt-0.5 leading-tight">Critical review ratings</span>
								<span className="text-2xl sm:text-3xl font-semibold text-red-600 mt-2 block leading-none">{metrics.overall.below3.toLocaleString()}</span>
							</div>
						</div>
						<div className="w-24 h-12 flex-shrink-0 self-center">
							<ResponsiveContainer width="100%" height="100%">
								<AreaChart data={below3SparklineData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
									<defs>
										<linearGradient id="colorBelow3" x1="0" y1="0" x2="0" y2="1">
											<stop offset="5%" stopColor="#EF4444" stopOpacity={0.25}/>
											<stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
										</linearGradient>
									</defs>
									<Area type="monotone" dataKey="count" stroke="#EF4444" strokeWidth={1.8} fillOpacity={1} fill="url(#colorBelow3)" dot={false} />
								</AreaChart>
							</ResponsiveContainer>
						</div>
					</div>
				</div>

				{/* 3. Overall Above 4 */}
				<div className="bg-white border border-gray-200/80 rounded-3xl p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 flex flex-col justify-between relative overflow-hidden group">
					<div className="flex items-start justify-between gap-4">
						<div className="flex items-start gap-4">
							<div className="w-12 h-12 rounded-full bg-green-50 border border-green-200 text-green-600 flex items-center justify-center flex-shrink-0">
								<Smile className="w-5 h-5" />
							</div>
							<div>
								<span className="text-xs font-semibold text-gray-700 block leading-tight">Above 4 Star</span>
								<span className="text-[10px] font-medium text-gray-500 block mt-0.5 leading-tight">Positive review ratings</span>
								<span className="text-2xl sm:text-3xl font-semibold text-green-600 mt-2 block leading-none">{metrics.overall.above4.toLocaleString()}</span>
							</div>
						</div>
						<div className="w-24 h-12 flex-shrink-0 self-center">
							<ResponsiveContainer width="100%" height="100%">
								<AreaChart data={above4SparklineData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
									<defs>
										<linearGradient id="colorAbove4" x1="0" y1="0" x2="0" y2="1">
											<stop offset="5%" stopColor="#10B981" stopOpacity={0.25}/>
											<stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
										</linearGradient>
									</defs>
									<Area type="monotone" dataKey="count" stroke="#10B981" strokeWidth={1.8} fillOpacity={1} fill="url(#colorAbove4)" dot={false} />
								</AreaChart>
							</ResponsiveContainer>
						</div>
					</div>
				</div>
			</div>

			{/* ── Row 2: Highlights Charts (Months vs Weeks side by side) ── */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 animate-slideIn">
				{/* Top Positive Month */}
				<div className="bg-white border border-gray-200/80 rounded-3xl p-6 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
					<div className="flex flex-col flex-1">
						<div className="flex items-center gap-3 mb-6">
							<div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center flex-shrink-0 shadow-md">
								<Trophy className="w-5 h-5" />
							</div>
							<div>
								<h3 className="font-semibold text-gray-900 text-base leading-tight">Top-Positive Rated Month</h3>
								<span className="text-xs text-gray-500 font-medium">in Last 3 Months</span>
							</div>
						</div>
						<span className="text-3xl font-semibold text-indigo-600 mt-2 tracking-tight">{metrics.highlights.topMonth}</span>
						<span className="text-xs text-gray-500 font-medium mt-1">{topMonthCount.toLocaleString()} ratings</span>
					</div>
					<div className="w-full md:w-60 h-44 flex-shrink-0">
						<ResponsiveContainer width="100%" height="100%">
							<BarChart data={metrics.last3MonthsBars || []} margin={{ top: 35, right: 5, left: 5, bottom: 5 }}>
								<XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#4B5563', fontWeight: 'normal' }} />
								<Tooltip cursor={{ fill: 'transparent' }} />
								<Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={32}>
									{(metrics.last3MonthsBars || []).map((entry, index) => {
										const maxVal = Math.max(...(metrics.last3MonthsBars || []).map(e => e.count));
										const isHighest = entry.count === maxVal && maxVal > 0;
										return (
											<Cell key={`cell-${index}`} fill={isHighest ? '#6366F1' : '#E0E7FF'} />
										);
									})}
									<LabelList dataKey="count" content={<CustomBarLabel data={metrics.last3MonthsBars || []} />} />
								</Bar>
							</BarChart>
						</ResponsiveContainer>
					</div>
				</div>

				{/* Top Negative Week */}
				<div className="bg-white border border-gray-200/80 rounded-3xl p-6 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
					<div className="flex flex-col flex-1">
						<div className="flex items-center gap-3 mb-6">
							<div className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center flex-shrink-0 shadow-md">
								<AlertCircle className="w-5 h-5" />
							</div>
							<div>
								<h3 className="font-semibold text-gray-900 text-base leading-tight">Top-Negative Rated Week</h3>
								<span className="text-xs text-gray-500 font-medium">in Last 12 Weeks</span>
							</div>
						</div>
						<span className="text-orange-600 font-semibold text-lg md:text-xl mt-2 tracking-tight">{metrics.highlights.topWeek}</span>
						<span className="text-xs text-gray-500 font-medium mt-1">{topWeekCount.toLocaleString()} ratings below 3 star</span>
					</div>
					<div className="w-full md:w-64 h-44 flex-shrink-0">
						<ResponsiveContainer width="100%" height="100%">
							<LineChart data={metrics.last12WeeksLines || []} margin={{ top: 25, right: 15, left: 10, bottom: 5 }}>
								<CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
								<YAxis axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#6B7280', fontWeight: 'normal' }} width={20} />
								<XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#6B7280', fontWeight: 'normal' }} />
								<Tooltip content={<CustomTooltip />} />
								<Line
									type="monotone"
									dataKey="count"
									stroke="#F97316"
									strokeWidth={2}
									dot={{ stroke: '#F97316', strokeWidth: 1.5, r: 3, fill: '#FFF' }}
									activeDot={{ r: 5 }}
								>
									<LabelList dataKey="count" content={<CustomPeakLabel data={metrics.last12WeeksLines || []} />} />
								</Line>
							</LineChart>
						</ResponsiveContainer>
					</div>
				</div>
			</div>

			{/* ── Row 3: Filtered vs Daily Groups (Grouped split columns side by side) ── */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 animate-slideIn">
				{/* Selected Date Range card */}
				<div className="bg-white border border-gray-200/85 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all">
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-4 border-b border-gray-100 gap-4">
						<div className="flex items-center gap-3">
							<div className="p-2.5 bg-blue-600 text-white rounded-xl shadow-md flex-shrink-0">
								<Calendar className="w-5 h-5" />
							</div>
							<div>
								<h3 className="font-semibold text-gray-900 text-base leading-tight">This</h3>
								<span className="text-xs text-gray-500 font-medium block mt-0.5">{getFormattedDateRangeLabel()}</span>
							</div>
						</div>
						
						{/* Date Selector Dropdown located below / next to the card header */}
						<div className="relative self-start sm:self-auto flex items-center gap-2">
							<div className="flex items-center gap-2 bg-white border border-gray-200 hover:border-gray-300 px-3 py-1.5 rounded-xl shadow-sm text-xs font-semibold text-gray-700 transition-all duration-200 justify-between cursor-pointer min-w-[140px]">
								<span className="text-xs font-semibold text-gray-700">
									{filter === "all" ? "All Time" :
									 filter === "today" ? "Today" :
									 filter === "yesterday" ? "Yesterday" :
									 filter === "last7days" ? "Last 7 Days" :
									 filter === "last30days" ? "Last 30 Days" :
									 filter === "thismonth" ? "This Month" :
									 filter === "custom" ? "Custom Range" : "Filter Period"}
								</span>
								<ChevronDown className="w-3.5 h-3.5 text-gray-400" />
							</div>
							<select
								value={filter}
								onChange={(e) => setFilter(e.target.value)}
								className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
							>
								<option value="all">All Time</option>
								<option value="today">Today</option>
								<option value="yesterday">Yesterday</option>
								<option value="last7days">Last 7 Days</option>
								<option value="last30days">Last 30 Days</option>
								<option value="thismonth">This Month</option>
								<option value="custom">Custom Range</option>
							</select>

							{filter === "custom" && (
								<div className="flex items-center gap-1.5 animate-fadeIn bg-white p-1 rounded-xl border border-gray-200 shadow-sm ml-1">
									<input
										type="date"
										value={customStart}
										onChange={(e) => setCustomStart(e.target.value)}
										className="px-1.5 py-0.5 rounded border border-gray-200 text-[10px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
									/>
									<span className="text-gray-500 text-[10px] font-semibold">to</span>
									<input
										type="date"
										value={customEnd}
										onChange={(e) => setCustomEnd(e.target.value)}
										className="px-1.5 py-0.5 rounded border border-gray-200 text-[10px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
									/>
								</div>
							)}
						</div>
					</div>
					{isUpdatingPeriod ? (
						<div className="flex items-center justify-center py-8">
							<div className="flex items-center gap-3 bg-indigo-50/50 border border-indigo-100 px-4 py-2.5 rounded-2xl shadow-sm animate-pulse">
								<div className="w-5 h-5 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
								<span className="text-xs font-bold text-indigo-600 tracking-wide">Updating Period Data...</span>
							</div>
						</div>
					) : (
						<div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-150 mt-6 gap-6 md:gap-0">
							{/* Total Received */}
							<div className="flex items-start gap-3.5 pb-4 md:pb-0 md:px-5 first:pl-0">
								<div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 shadow-md">
									<Star className="w-5 h-5 fill-current" />
								</div>
								<div className="flex flex-col min-w-0">
									<span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider block leading-tight whitespace-normal break-words">Total Ratings</span>
									<span className="text-2xl font-semibold text-gray-900 mt-0.5 block">{metrics.thisPeriod.total.toLocaleString()}</span>
								</div>
							</div>
							{/* Below 3 */}
							<div className="flex items-start gap-3.5 py-4 md:py-0 md:px-5">
								<div className="w-10 h-10 rounded-full bg-red-50 text-red-500 border border-red-200 flex items-center justify-center flex-shrink-0">
									<Frown className="w-5 h-5" />
								</div>
								<div className="flex flex-col min-w-0">
									<span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider block leading-tight whitespace-normal break-words">Below 3 Star</span>
									<span className="text-2xl font-semibold text-gray-900 mt-0.5 block">{metrics.thisPeriod.below3.toLocaleString()}</span>
								</div>
							</div>
							{/* Above 4 */}
							<div className="flex items-start gap-3.5 pt-4 md:pt-0 md:px-5 last:pr-0">
								<div className="w-10 h-10 rounded-full bg-green-50 text-green-500 border border-green-200 flex items-center justify-center flex-shrink-0">
									<Smile className="w-5 h-5" />
								</div>
								<div className="flex flex-col min-w-0">
									<span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider block leading-tight whitespace-normal break-words">Above 4 Star</span>
									<span className="text-2xl font-semibold text-gray-900 mt-0.5 block">{metrics.thisPeriod.above4.toLocaleString()}</span>
								</div>
							</div>
						</div>
					)}
				</div>

				{/* Today card */}
				<div className="bg-white border border-gray-200/85 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all">
					<div className="flex items-center gap-3 pb-4 border-b border-gray-100">
						<div className="p-2.5 bg-emerald-600 text-white rounded-xl shadow-md">
							<Clock className="w-5 h-5" />
						</div>
						<div>
							<h3 className="font-semibold text-gray-900 text-base leading-tight">Today</h3>
							<span className="text-xs text-gray-500 font-medium block mt-0.5">
								{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
							</span>
						</div>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-150 mt-6 gap-6 md:gap-0">
						{/* Total Received */}
						<div className="flex items-start gap-3.5 pb-4 md:pb-0 md:px-5 first:pl-0">
							<div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 shadow-md">
								<Users className="w-5 h-5" />
							</div>
							<div className="flex flex-col min-w-0">
								<span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider block leading-tight whitespace-normal break-words">Today Ratings</span>
								<span className="text-2xl font-semibold text-gray-900 mt-0.5 block">{metrics.today.total.toLocaleString()}</span>
							</div>
						</div>
						{/* Below 3 */}
						<div className="flex items-start gap-3.5 py-4 md:py-0 md:px-5">
							<div className="w-10 h-10 rounded-full bg-red-50 text-red-500 border border-red-200 flex items-center justify-center flex-shrink-0">
								<Frown className="w-5 h-5" />
							</div>
							<div className="flex flex-col min-w-0">
								<span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider block leading-tight whitespace-normal break-words">Below 3 Star</span>
								<span className="text-2xl font-semibold text-gray-900 mt-0.5 block">{metrics.today.below3.toLocaleString()}</span>
							</div>
						</div>
						{/* Above 4 */}
						<div className="flex items-start gap-3.5 pt-4 md:pt-0 md:px-5 last:pr-0">
							<div className="w-10 h-10 rounded-full bg-green-50 text-green-500 border border-green-200 flex items-center justify-center flex-shrink-0">
								<Smile className="w-5 h-5" />
							</div>
							<div className="flex flex-col min-w-0">
								<span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider block leading-tight whitespace-normal break-words">Above 4 Star</span>
								<span className="text-2xl font-semibold text-gray-900 mt-0.5 block">{metrics.today.above4.toLocaleString()}</span>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* ── Milestone Tracker ── */}
			<div className="mb-6">
				<Milestone />
			</div>

			{/* ── Reviews Per Team Member ── */}
			<ReviewsPerUserTable reviewsPerUser={reviewsPerUser} />

			

			<style>{`
				@keyframes slideIn {
					from {
						transform: translateY(20px);
						opacity: 0;
					}
					to {
						transform: translateY(0);
						opacity: 1;
					}
				}

				@keyframes fadeIn {
					from {
						opacity: 0;
					}
					to {
						opacity: 1;
					}
				}

				.animate-slideIn {
					animation: slideIn 0.7s ease-out;
				}

				.animate-fadeIn {
					animation: fadeIn 0.5s ease-out;
				}
			`}</style>
		</div>
	);
}
